// src/pages/ResumenPage.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import { Url } from "../components/Url";
import ContextStates from "../context/ContextStates";
import { useLocation, useNavigate } from "react-router-dom";

// ===== Helpers base =====
const num = (v) => (typeof v === "number" ? v : Number.parseFloat(String(v || 0))) || 0;
const formatARS = (n) =>
    Number(n).toLocaleString("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 2 });

// ======= Helpers de COMBOS (idénticos a FormPedido) =======
const buildQtyByCombo = (items) => {
    const map = {};
    for (const it of items || []) {
        const nc = it?.numerocombo;
        if (!nc) continue;
        const q = Number(it.cantidad) || 0;
        map[nc] = (map[nc] || 0) + q;
    }
    return map;
};

const unitFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let unit = Number(base) || 0;
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) unit = Number(t.precio_unitario) || 0;
        else break;
    }
    return unit;
};

const unitByCombo = (item, selfQty, qtyByCombo) => {
    const base = Number(item.precio ?? item.precio_base ?? 0) || 0;
    if (item.numerocombo && item.precios_combo?.length) {
        const groupQty = qtyByCombo[item.numerocombo] || 0;
        return unitFromProductComboTiers(base, item.precios_combo, groupQty);
    }
    return base;
};

const comisionFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let comision = Number(base) || 0;
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) comision = Number(t.comision) || 0;
        else break;
    }
    return comision;
};

const comisionByCombo = (item, selfQty, qtyByCombo) => {
    const base = Number(item.comision) || 0;
    if (item.numerocombo && item.precios_combo?.length) {
        const groupQty = qtyByCombo[item.numerocombo] || 0;
        return comisionFromProductComboTiers(base, item.precios_combo, groupQty);
    }
    return base;
};

const subtotalByCombo = (item, qty, qtyByCombo) => {
    const q = Number(qty) || 0;
    if (q <= 0) return 0;
    const unit = unitByCombo(item, q, qtyByCombo);
    return unit * q;
};

// ==========================================================
export default function ResumenPage() {
    const [guardar, setGuardar] = useState(true)
    const {
        clienteSelected,
        productosSeleccionados,
        setProductosSeleccionados,
        modify,
        setModify,
        idUsuario,
    } = useContext(ContextStates);

    const location = useLocation();
    const navigate = useNavigate();
    const passedItems = Array.isArray(location?.state?.items) ? location.state.items : null;

    const [coords, setCoords] = useState({ lat: null, lng: null });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        console.log("cliente seleccionado ", clienteSelected)
        if (!("geolocation" in navigator)) return;
        navigator.geolocation.getCurrentPosition(
            (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => { },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
        );
    }, []);

    // Mapa de cantidades por combo (para recalcular cuando NO vienen items)
    const qtyByCombo = useMemo(
        () => buildQtyByCombo(productosSeleccionados),
        [productosSeleccionados]
    );

    // Ítems:
    // - Si vienen `passedItems`, se usan tal cual (ya traen unit/subtotal).
    // - Si NO vienen, reconstruimos con la misma lógica de FormPedido (combos).
    const items = useMemo(() => {
        if (passedItems) {
            return passedItems.map((it) => ({
                id: it.id,
                descripcion: it.descripcion,
                cantidad: num(it.cantidad),
                precio_unitario_final: num(it.precio_unitario_final ?? it.precio_unitario ?? it.precio),
                comision_porcentaje: num(it.comision_porcentaje),
                comision: num(it.comision),
                subtotal: num(it.subtotal ?? num(it.precio_unitario_final ?? it.precio) * num(it.cantidad)),
            }));
        }

        const src = Array.isArray(productosSeleccionados) ? productosSeleccionados : [];
        return src.map((p) => {
            const q = num(p.cantidad);
            const unit = unitByCombo(p, q, qtyByCombo);
            const comisionPorc = comisionByCombo(p, q, qtyByCombo);
            const subtotal = unit * q;
            const comisionMonto = (subtotal * comisionPorc) / 100;
            return {
                id: p.id,
                descripcion: p.descripcion,
                cantidad: q,
                numerocombo: p.numerocombo ?? null,
                precio_unitario_final: unit,
                comision_porcentaje: comisionPorc,
                comision: comisionMonto,
                subtotal: subtotal,
            };
        });
    }, [passedItems, productosSeleccionados, qtyByCombo]);

    const total = useMemo(() => items.reduce((acc, it) => acc + num(it.subtotal), 0), [items]);

    const guardarDatos = async () => {
        if (guardar) {
            setGuardar(false)
            try {
                setSaving(true);

                const productosPayload = items.map((it) => ({
                    id: it.id,
                    cantidad: it.cantidad,
                    precio_unitario_final: it.precio_unitario_final,
                    comision_porcentaje: it.comision_porcentaje,
                    comision: it.comision,
                    subtotal: it.subtotal,
                }));

                const formData = new FormData();
                formData.append("idcliente", String(clienteSelected?.id));
                formData.append("idusuario", String(idUsuario ?? 1));
                if (coords.lat != null) formData.append("lat", String(coords.lat ?? ''));
                if (coords.lng != null) formData.append("lng", String(coords.lng ?? ''));
                formData.append("productos", JSON.stringify(productosPayload));
                formData.append("total", String(total));
                formData.append("idpedido", String(clienteSelected?.idpedido ?? 0));

                const res = await fetch(Url + "api/guardarpedido.php", { method: "POST", body: formData });
                const text = await res.text();
                console.log(text)
                let data;
                try {
                    data = JSON.parse(text);
                } catch {
                    throw new Error("Respuesta no-JSON del servidor: " + text);
                    // Si preferís, podrías mostrar el `text` en un modal para depurar.
                }

                if (!data?.success) {
                    alert(data?.message || "No se pudo guardar el pedido.");
                    setSaving(false);
                    return;
                }

                setProductosSeleccionados([]);
                setModify((m) => m + 1);
                alert(`Pedido #${data.idpedido} guardado con éxito`);
                setSaving(false);
                navigate("/pedidos");
            } catch (err) {
                console.error(err);
                alert("No se pudo guardar el pedido. Intentalo nuevamente.");
                setSaving(false);
            }

        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="sticky top-0 z-10 bg-white border-b border-gray-200">
                <div className="mx-auto max-w-5xl px-4 py-3">
                    <h1 className="text-lg font-semibold">Resumen del pedido</h1>
                    <p className="text-sm text-gray-600">
                        Cliente: <span className="font-medium">{clienteSelected?.nombre ?? "—"}</span>
                    </p>
                    {clienteSelected?.idpedido ? (
                        <p className="text-xs text-gray-500 mt-1">Editando pedido #{clienteSelected.idpedido}</p>
                    ) : null}
                </div>
            </header>

            <main className="mx-auto max-w-5xl px-4 pb-28">
                <ul className="divide-y divide-gray-200 bg-white shadow-sm rounded-lg mt-4">
                    {items.map((it) => (
                        <li key={it.id} className="p-4">
                            <div className="flex items-baseline justify-between">
                                <p className="font-medium text-gray-900">{it.descripcion}</p>
                                <p className="text-sm text-gray-700">
                                    {it.cantidad} × <span className="font-semibold">{formatARS(it.precio_unitario_final)}</span>
                                </p>
                            </div>
                            <div className="mt-1 text-sm">
                                <span className="text-gray-500 mr-1">Subtotal:</span>
                                <span className="font-semibold text-gray-900">{formatARS(it.subtotal)}</span>
                            </div>
                            {it.comision > 0 && (
                                <div className="mt-1 text-sm">
                                    <span className="text-gray-500 mr-1">Comisión ({it.comision_porcentaje}%):</span>
                                    <span className="font-medium text-green-700">{formatARS(it.comision)}</span>
                                </div>
                            )}
                        </li>
                    ))}
                    {!items.length && (
                        <li className="p-6 text-center text-gray-500">No hay productos seleccionados.</li>
                    )}
                </ul>

                <div className="mt-6 flex items-center justify-between">
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-semibold">{formatARS(total)}</p>
                </div>
            </main>

            <footer className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white">
                <div className="mx-auto max-w-5xl px-4 py-3 flex items-center justify-end gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center justify-center rounded-lg px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                        Volver
                    </button>
                    <button
                        onClick={guardarDatos}
                        disabled={!items.length || total <= 0 || saving}
                        className="inline-flex items-center justify-center rounded-lg bg-gray-900 px-4 py-2 text-white font-medium disabled:opacity-50"
                    >
                        {saving ? "Guardando..." : "Finalizar pedido"}
                    </button>
                </div>
            </footer>
        </div>
    );
}
